import { useQuery } from "react-query";
import difference from "lodash/difference";
import apiService from "../services/apiService";
import { useLang } from "./LangHooks";
import { uniq } from "lodash";

export function usePage(path) {
  const lang = useLang();
  const { data: page } = useQuery(["page", path, lang], async () =>
    apiService.loadPage(path, lang)
  );
  return page || {};
}

export function usePageFragments(limit) {
  const lang = useLang();
  const { data: fragments } = useQuery(["pageFragments", lang], async () =>
    apiService.loadPageFragments(lang)
  );
  return fragments.slice(0, limit);
}

export function usePageFragment(id) {
  const lang = useLang();
  const { data: fragments } = useQuery(["pageFragments", lang], async () =>
    apiService.loadPageFragments(lang)
  );
  return fragments.find(fgmt => fgmt._id === id);
}

export function useStories(limit) {
  const lang = useLang();
  let { data: stories } = useQuery(["stories", lang], async () =>
    apiService.loadStories(lang)
  );
  //TODO: Update terminology in Cockpit, then remove this
  stories = stories.map(story => {
    if (!story.category) {
      story.category = story.segment;
    }
    if (Array.isArray(story.type) && story.type.includes("story")) {
      story.type[story.type.indexOf("story")] = "article";
      story.type = uniq(story.type);
    }
    return story;
  });
  return stories.slice(0, limit);
}

export function useStoryByPath(slug) {
  const lang = useLang();
  const { data: _story } = useQuery(["story", slug, lang], async () => {
    return await apiService.loadStoryByPath(slug, lang);
  });
  const story = { ..._story };
  if (!story.category) {
    story.category = story.segment;
  }
  return story || {};
}

export function useStoryById(id) {
  const lang = useLang();
  const { data: _story } = useQuery(["story", id, lang], async () => {
    //await delay(3000); //For testing
    return await apiService.loadStoryById(id, lang);
  });
  const story = { ..._story };
  if (!story.category) {
    story.category = story.segment;
  }
  return story || {};
}

export function useLottieAnimation(id) {
  const { data: animation } = useQuery(["animation", id], async () => {
    if (id) {
      const animation = await apiService.loadAnimation(id);
      delete animation.code;
      return animation;
    } else {
      return false;
    }
  });
  return animation || {};
}

export function useGuides(limit) {
  const lang = useLang();
  const { data: results } = useQuery(["guides", lang], async () =>
    apiService.loadGuides(lang)
  );
  return results.guides.slice(0, limit);
}

export function useMatchingGuides(category, tags) {
  const lang = useLang();
  const { data: results } = useQuery(["guides"], async () =>
    apiService.loadGuides(lang)
  );
  results.guides.forEach(guide => {
    console.debug(
      "useMatchingGuides:",
      category,
      tags,
      guide.tags,
      difference(guide.tags, tags)
    );
  });
  return results.guides.filter(
    guide =>
      (category !== undefined ? guide?.category?._id === category : true) &&
      (tags !== undefined ? difference(guide.tags, tags).length === 0 : true)
  );
}

export function useGuide(path) {
  const lang = useLang();
  const { data: results } = useQuery(["guides", lang], async () =>
    apiService.loadGuides(lang)
  );
  return results.guides.find(guide => guide.path === path);
}

export function useGuideCategories() {
  const lang = useLang();
  const { data: results } = useQuery(["guides", lang], async () =>
    apiService.loadGuides(lang)
  );
  return results.categories;
}

export function useGuideCategoryByPath(path) {
  const lang = useLang();
  const { data: results } = useQuery(["guides"], async () =>
    apiService.loadGuides(lang)
  );
  const entry = Object.entries(results.categories).find(
    ([_, cat]) => cat.path === path
  );
  return entry ? entry[1] : false;
}
